import * as React from 'react'
import { useIsGlobalAdmin } from '@/hooks/useIsGlobalAdmin'

import {
  CLIENT_TAB_KEYS,
  type ClientTabCounts,
  type ClientTabKey,
} from '@/features/patients/components/ClientSheetTabs'
import { timelineKinds } from '@/features/patients/components/ClientTimelineFilters'
import type { PatientRecord } from '@/features/patients/types'
import { mergeSourceIds } from '@/features/patients/utils/sourceIds'
import { useClientActivity, type ClientActivity } from './useClientActivity'
import { useClientClinical, type ClientClinical } from './useClientClinical'
import { useClientHistory, type ClientHistory } from './useClientHistory'

export interface ClientSheetData {
  history: ClientHistory
  activity: ClientActivity
  clinical: ClientClinical
  /** Tabs this viewer may open, in display order. */
  tabs: readonly ClientTabKey[]
  counts: ClientTabCounts
}

/** No pill while a count is loading, or for an empty tab. */
function pill(count: number | null | undefined): number | undefined {
  return count ? count : undefined
}

/**
 * Everything the client sheet fetches for one client, plus which tabs the
 * viewer may open and the count each tab shows.
 */
export function useClientSheetData(
  patient: PatientRecord | undefined
): ClientSheetData {
  const history = useClientHistory(patient?.id)
  const activity = useClientActivity(patient?.id)
  const clinical = useClientClinical(patient?.id)
  // Match the current Documents route gate; the sheet already requires
  // data.clients.view. Broader rollout can use the same capability later.
  const canDocuments = useIsGlobalAdmin()

  const { appointments } = history
  // The visit mix makes the tab useful on visit history alone.
  const canClinical =
    history.visits.allowed ||
    clinical.conditions.allowed ||
    clinical.labs.allowed

  const tabs = React.useMemo(() => {
    const allowed: Record<ClientTabKey, boolean> = {
      overview: true,
      // Duties need no capability beyond the sheet's, so there is always
      // something the timeline can show.
      activity: true,
      appointments: appointments.allowed,
      documents: canDocuments,
      clinical: canClinical,
      identifiers: true,
    }
    return CLIENT_TAB_KEYS.filter((key) => allowed[key])
  }, [appointments.allowed, canClinical, canDocuments])

  // The tab counts what its "All" filter shows, once every count is in.
  const kindCounts = timelineKinds(activity, history).map((kind) => kind.count)
  const timelineTotal = kindCounts.every((count) => count !== null)
    ? kindCounts.reduce<number>((sum, count) => sum + (count ?? 0), 0)
    : null

  const counts = React.useMemo<ClientTabCounts>(
    () => ({
      activity: pill(timelineTotal),
      appointments:
        appointments.allowed && !appointments.isLoading
          ? pill(appointments.rows.length)
          : undefined,
      identifiers: patient
        ? pill(mergeSourceIds(patient.source_ids).length)
        : undefined,
    }),
    [timelineTotal, appointments, patient]
  )

  return {
    history,
    activity,
    clinical,
    tabs,
    counts,
  }
}
